import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, FileText, History, BarChart3, MessageSquareReply, Building2, Store, Settings, HelpCircle, ShieldCheck, Plus, Sparkles, MoreHorizontal, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import TrialBanner from "@/components/TrialBanner";
import { api, usageText } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const Brand = ({ light = true, className = "h-9" }) => (
  <div className="flex flex-col items-start gap-1">
    <img src={light ? "/logo-full-light.png" : "/logo-full.png"} alt="Contestafood" data-testid="brand-logo" className={`${className} w-auto`} />
    <p className={`pl-1 text-[8px] font-bold uppercase tracking-[0.22em] ${light ? "text-white/60" : "text-slate-500"}`}>Defesa para restaurantes</p>
  </div>
);

const WORKSPACE = [
  { to: "/", label: "Visão geral", icon: LayoutGrid, id: "nav-overview", end: true },
  { to: "/nova-analise", label: "Nova análise", icon: FileText, id: "nav-new-analysis" },
  { to: "/recursos", label: "Histórico", icon: History, id: "nav-history", badge: true },
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, id: "nav-dashboard" },
  { to: "/respostas", label: "Respostas públicas", icon: MessageSquareReply, id: "nav-replies" },
];
const ACCOUNT = [
  { to: "/restaurante", label: "Dados do restaurante", icon: Building2, id: "nav-restaurant" },
  { to: "/lojas", label: "Lojas e cardápio", icon: Store, id: "nav-stores" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, id: "nav-settings" },
  { to: "/como-funciona", label: "Como funciona", icon: HelpCircle, id: "nav-how" },
];

const linkCls = ({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-amber-500 text-white shadow-[0_8px_20px_-10px_rgba(255,106,15,0.9)]" : "text-white/75 hover:bg-white/5 hover:text-white"}`;

const maskCnpj = (c) => (c ? `CNPJ •••.•••.•••/${c.replace(/\D/g, "").slice(8, 12) || "••••"}-${c.replace(/\D/g, "").slice(12, 14) || "••"}` : "Complete seus dados");

export default function AppShell() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [count, setCount] = useState(0);
  useEffect(() => { api.get("/overview").then((r) => setCount(r.data.total_cases)).catch((e) => console.warn("overview count failed", e)); }, [loc.pathname]);
  const account = user?.role === "admin" ? [...ACCOUNT, { to: "/admin", label: "Administração", icon: ShieldCheck, id: "nav-admin" }] : ACCOUNT;
  const display = user?.restaurant_name || user?.name || "";
  const initials = display.split(/[\s&]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const logout = async () => { await signOut(); nav("/login"); };

  const Item = (n) => (
    <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={n.id}>
      <n.icon className="h-[18px] w-[18px]" /> <span className="flex-1">{n.label}</span>
      {n.badge && count > 0 && <span className="rounded-full bg-white/10 px-1.5 text-[10px] text-white/80" data-testid="nav-history-count">{count}</span>}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[232px] flex-col overflow-y-auto bg-slate-900 px-3.5 py-5 md:flex">
        <div className="px-2"><Brand /></div>
        <Link to="/nova-analise" data-testid="sidebar-new-case-btn" className="mt-7 flex h-11 shrink-0 items-center gap-2 rounded-xl bg-[#FBF3E6] px-5 text-[15px] font-medium text-slate-900 transition-transform hover:-translate-y-0.5"><Plus className="h-4 w-4" /> Nova contestação</Link>
        <p className="mb-2 mt-8 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Workspace</p>
        <nav className="flex flex-col gap-1">{WORKSPACE.map(Item)}</nav>
        <p className="mb-2 mt-7 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Conta</p>
        <nav className="flex flex-col gap-1">{account.map(Item)}</nav>
        <div className="mt-auto pt-6">
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <p className="flex items-center gap-2 text-xs font-bold text-white"><Sparkles className="h-3.5 w-3.5 text-amber-400" /> Análise estratégica</p>
            <p className="mt-1 pl-5 text-[11px] leading-relaxed text-white/60">Organize os fatos e encontre a prova mais forte.</p>
            <p className="mt-2 pl-5 text-[10px] text-white/40" data-testid="sidebar-usage">{user?.plan?.name} · {usageText(user)}</p>
          </div>
          <div className="mt-3 flex items-center gap-3 border-t border-white/10 px-1 pt-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FBF3E6] text-xs font-extrabold text-slate-900">{initials}</div>
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{display}</p><p className="truncate text-[10px] text-white/50">{maskCnpj(user?.cnpj)}</p></div>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-btn" className="rounded p-1 text-white/60 hover:text-white"><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => nav("/restaurante")}>Dados do restaurante</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/configuracoes")}>Configurações</DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={logout}><LogOut className="mr-2 h-4 w-4" /> Sair</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-slate-50/90 px-4 py-3 backdrop-blur-lg md:hidden">
        <Brand light={false} />
        <div className="flex gap-2">
          <button data-testid="mobile-logout-btn" onClick={logout} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500"><LogOut className="h-5 w-5" /></button>
          <Link to="/nova-analise" data-testid="mobile-new-case-btn" className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white"><Plus className="h-5 w-5" /></Link>
        </div>
      </header>

      <main className="px-4 pb-28 pt-6 md:ml-[232px] md:px-8 md:pb-12 md:pt-8">
        <div className="mx-auto max-w-[1320px]"><TrialBanner /><Outlet /></div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-slate-200 bg-white/90 backdrop-blur-lg md:hidden">
        {[WORKSPACE[0], WORKSPACE[1], WORKSPACE[2], WORKSPACE[3], ACCOUNT[0]].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`m-${n.id}`} className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${isActive ? "text-amber-500" : "text-slate-500"}`}>
            <n.icon className="h-5 w-5" /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
